import React, { useState, useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { Box, Breadcrumbs } from "@mui/material";
import { GridColDef, GridCellParams } from "@mui/x-data-grid";
import { Formik, Form } from "formik";
import Grid from "@mui/material/Grid";
import * as Yup from "yup";
import SearchIcon from "@mui/icons-material/Search";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import YardIcon from '@mui/icons-material/Yard';
import DeleteIcon from "@mui/icons-material/Delete";
import TextField from "../../../components/TextField";
import Dropdown from "../../../components/Dropdown";
import GridTable from "../../../components/GridTable";
import ActionMenu from "../../../components/ActionMenu";
import Dialog from "../../../components/Dialog";
import Loader from "../../../components/Loader";
import Button from "../../../components/Button";
import { successMessage, errorMessage } from "../../../components/Toaster";
import { getAllCrops } from "../../crop/api";
import { CropData } from "../../crop/interface";
import { getVarietiesByCropId } from "../../crop/variety/api";
import { VarietyData } from "../../crop/variety/interface";
import { getCropsByCompanyID, attachCrop, detachCrop } from "./api";
import { ApiResponse, CropParams, CompanyCropData } from "./interface";
import { getErrorMessage } from "../../../utils/error";

const Crop: React.FC = () => {
    const location = useLocation();
    const { company_id } = useParams();
    const company_name = location.state?.company_name || "Company";
    const [companyCrops, setCompanyCrops] = useState<CompanyCropData[]>([]);
    const [crops, setCrops] = useState<CropData[]>([]);
    const [selectedCropId, setSelectedCropId] = useState<string | null>(null);
    const [varieties, setVarieties] = useState<VarietyData[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [isDialogOpen, setDialogOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<GridCellParams | null>(null);
    const [loading, setLoading] = useState({
        fetch: false,
        create: false,
        update: false,
        delete: false,
    });

    const validationSchema = Yup.object().shape({
        cropId: Yup.string().required("Crop is required"),
        varietyId: Yup.string().required("Variety is required"),
    });

    const columns: GridColDef[] = [
        { field: "crop_name", headerName: "Crop", flex: 1 },
        { field: "variety_name", headerName: "Variety", flex: 1 },
        {
            field: "actions",
            headerName: "Actions",
            renderCell: (params: GridCellParams) => (
                <ActionMenu
                    params={params}
                    actions={[
                        {
                            icon: <DeleteIcon color="error" />,
                            tooltip: "Delete",
                            onClick: () => handleDelete(params),
                        },
                    ]}
                />
            ),
            sortable: false,
            filterable: false,
            flex: 1,
        },
    ];

    useEffect(() => {
        const fetchCrops = async () => {
            setLoading(prev => ({ ...prev, fetch: true }));
            try {
                const res: ApiResponse<CompanyCropData[]> = await getCropsByCompanyID(company_id || "");
                if (res.status && res.data) {
                    setCompanyCrops(res.data);
                } else {
                    errorMessage(res.message || "Failed to fetch crops.");
                }
            } catch (error) {
                errorMessage(getErrorMessage(error) || "Error fetching crops");
            } finally {
                setLoading(prev => ({ ...prev, fetch: false }));
            }
        };

        fetchCrops();
    }, [company_id]);

    useEffect(() => {
        const fetchVarieties = async () => {
            if (!selectedCropId) return;
            setLoading(prev => ({ ...prev, varieties: true }));
            try {
                const res = await getVarietiesByCropId(selectedCropId);
                if (res.status && res.data) {
                    setVarieties(res.data);
                } else {
                    errorMessage(res.message || "Failed to fetch varieties.");
                }
            } catch (error) {
                errorMessage(getErrorMessage(error) || "Error fetching varieties");
            } finally {
                setLoading(prev => ({ ...prev, varieties: false }));
            }
        };

        fetchVarieties();
    }, [selectedCropId]);

    const openDialog = async () => {
        setDialogOpen(true);
        setLoading(prev => ({ ...prev, fetch: true }));
        try {
            const res: ApiResponse<CropData[]> = await getAllCrops();
            if (res.status && res.data) {
                setCrops(res.data);
            } else {
                errorMessage(res.message || "Failed to fetch crops.");
            }
        } catch (error) {
            errorMessage(getErrorMessage(error) || "Error fetching crops");
        } finally {
            setLoading(prev => ({ ...prev, fetch: false }));
        }
    };

    const closeDialog = (resetForm: () => void) => {
        resetForm();
        setSelectedCropId(null)
        setVarieties([])
        setDialogOpen(false);
    };

    const handleDelete = (params: GridCellParams) => {
        setItemToDelete(params);
        setDeleteOpen(true);
    };

    const handleDeleteCancel = () => {
        setDeleteOpen(false);
        setItemToDelete(null);
    };

    const confirmDialog = async (values: any, { resetForm }: { resetForm: () => void }) => {
        const success = await handleCreate(values);

        if (success) {
            closeDialog(resetForm);
        }
    };

    const handleCreate = async (values: any) => {
        setLoading(prev => ({ ...prev, create: true }));
        try {
            const newCrop: CropParams = {
                crop_id: values.cropId,
                variety_id: values.varietyId,
                company_id: company_id!,
            };
            const response = await attachCrop(newCrop);
            if (response.status && response.data) {
                const cropData = crops.find(crop => crop.crop_id === values.cropId);
                const varietyData = varieties.find(variety => variety.variety_id === values.varietyId);
                const crop = {
                    company_crop_id: response.data.company_crop_id,
                    crop_id: values.cropId,
                    crop_name: cropData?.crop_name || "",
                    variety_id: values.varietyId || "",
                    variety_name: varietyData?.variety_name || "",
                };
                setCompanyCrops(prev => [...prev, crop]);
                successMessage(response.message);
                return true;
            } else {
                errorMessage(response.message || "Failed to attach crop.");
                return false;
            }
        } catch (error) {
            errorMessage(getErrorMessage(error) || "Unexpected error occurred.");
            return false;
        } finally {
            setLoading(prev => ({ ...prev, create: false }));
        }
    };

    const handleDeleteConfirm = async () => {
        setLoading(prev => ({ ...prev, delete: true }));
        try {
            if (itemToDelete) {
                const response = await detachCrop(itemToDelete.row.company_crop_id);
                if (response.status) {
                    setCompanyCrops(prev =>
                        prev.filter(c => c.company_crop_id !== itemToDelete.row.company_crop_id)
                    );
                    successMessage(response.message);
                } else {
                    errorMessage(response.message || "Failed to detach crop.");
                }
            }
        } catch (error) {
            errorMessage(getErrorMessage(error) || "Unexpected error occurred.");
        } finally {
            setLoading(prev => ({ ...prev, delete: false }));
            setDeleteOpen(false);
        }
    };

    return (
        <>
            {loading.fetch ? (
                <Loader color="primary" size={40} />
            ) : (
                <Box>
                    <main className="m-a-20">
                        <div style={{ display: "flex", alignItems: "center", marginBottom: "30px" }}>
                            <Breadcrumbs
                                separator={<NavigateNextIcon fontSize="small" />}
                                aria-label="breadcrumb"
                                style={{ fontSize: "18px", fontWeight: "bold" }}
                            >
                                <Link to="/company">Company</Link>
                                <Link to={`/company`}>
                                    {company_name}
                                </Link>
                                <Link to={`/company/${company_id}/crops`} state={{ company_name }}>
                                    Crops
                                </Link>
                            </Breadcrumbs>
                        </div>
                        <div className="d-flex justify-between w-100">
                            <TextField
                                className="w-25"
                                label="Search"
                                variant="outlined"
                                value={searchQuery}
                                size="small"
                                startIcon={<SearchIcon />}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            <Button label="Add" variant="contained" color="primary" onClick={openDialog} />
                        </div>
                        <div className="ag-theme-alpine m-t-20" style={{ height: "100%", width: "100%" }}>
                            <GridTable
                                rows={companyCrops}
                                columns={columns}
                                pageSize={10}
                                searchQuery={searchQuery}
                                setSearchQuery={setSearchQuery}
                                getRowId={(row) => row.company_crop_id}
                            />
                        </div>
                    </main>
                </Box>
            )}
            <Formik
                initialValues={{
                    cropId: "",
                    varietyId: ""
                }}
                validationSchema={validationSchema}
                onSubmit={confirmDialog}
                enableReinitialize
            >
                {({ values, errors, touched, setFieldValue, handleSubmit, resetForm }) => (
                    <Dialog
                        open={isDialogOpen}
                        onClose={() => closeDialog(resetForm)}
                        onConfirm={handleSubmit}
                        title="Attach New Crop"
                        confirmButtonText="Attach"
                        cancelButtonText="Cancel"
                        icon={<YardIcon />}
                        message={
                            <Box padding={4}>
                                {loading.create || loading.update ? (
                                    <Loader color="primary" size={40} />
                                ) : (
                                    <Form>
                                        <Grid container spacing={2}>
                                            <Grid size={6}>
                                                <Dropdown
                                                    options={crops.map((crop) => ({
                                                        id: crop.crop_id,
                                                        name: crop.crop_name,
                                                    }))}
                                                    value={values.cropId ?? ""}
                                                    onChange={(id) => {
                                                        const selectedId = typeof id === "string" ? id : String(id);
                                                        setFieldValue("cropId", id);
                                                        setSelectedCropId(selectedId);
                                                    }}
                                                    label="Crop"
                                                    required
                                                    error={Boolean(touched.cropId && errors.cropId)}
                                                    helperText={
                                                        touched.cropId && errors.cropId
                                                            ? String(errors.cropId)
                                                            : undefined
                                                    }
                                                />
                                            </Grid>
                                            <Grid size={6}>
                                                {varieties.length > 0 &&
                                                    <Dropdown
                                                        options={varieties.map((variety) => ({
                                                            id: variety.variety_id,
                                                            name: variety.variety_name,
                                                        }))}
                                                        value={values.varietyId ?? ""}
                                                        onChange={(id) => setFieldValue("varietyId", id)}
                                                        label="Variety"
                                                        required
                                                        error={Boolean(touched.varietyId && errors.varietyId)}
                                                        helperText={
                                                            touched.varietyId && errors.varietyId
                                                                ? String(errors.varietyId)
                                                                : undefined
                                                        }
                                                    />}
                                            </Grid>
                                        </Grid>
                                    </Form>
                                )}
                            </Box>
                        }
                    />
                )}
            </Formik>
            <Dialog
                open={deleteOpen}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                title={!loading.delete ? "Dettach Crop" : ""}
                confirmButtonText={!loading.delete ? "Confirm" : ""}
                cancelButtonText={!loading.delete ? "Cancel" : ""}
                icon={<DeleteIcon />}
                message={
                    <Box padding={2}>
                        {loading.delete ? (
                            <Loader color="primary" size={40} />
                        ) : (
                            "Are you sure you want to dettach this Crop?"
                        )}
                    </Box>
                }
            />
        </>
    );
};

export default Crop;
